import { WireScopeSchema, type WireScope } from '@verantos-dev/feature-epi-chat';

import type { Scope } from '../types';

/**
 * Convert the URL-derived camelCase ``Scope`` to its snake_case wire shape
 * before it crosses the Socket.IO boundary. Parses through Zod so any
 * future drift between the FE union and the wire schema is caught at the
 * boundary (per the project's "Zod at trust boundaries" rule).
 *
 * Stable across renders when the input ``Scope`` is referentially stable —
 * `useCurrentScope` memoizes on path identity, so chaining
 * ``useMemo(() => toWireScope(scope), [scope])`` keeps ``WireScope``
 * ref-stable too, which is what ``useEpiChatPage`` relies on to dedupe
 * ``scope_changed`` emissions.
 */
export const toWireScope = (scope: Scope): WireScope => {
  switch (scope.kind) {
    case 'project':
      return WireScopeSchema.parse({ kind: 'project', project_id: scope.projectId });
    case 'cohort':
      return WireScopeSchema.parse({
        kind: 'cohort',
        project_id: scope.projectId,
        cohort_id: scope.cohortId,
      });
    case 'variable':
      return WireScopeSchema.parse({
        kind: 'variable',
        project_id: scope.projectId,
        variable_id: scope.variableId,
      });
    case 'concept_group':
      return WireScopeSchema.parse({
        kind: 'concept_group',
        project_id: scope.projectId,
        concept_group_id: scope.conceptGroupId,
      });
    case 'dataset':
      return WireScopeSchema.parse({
        kind: 'dataset',
        project_id: scope.projectId,
        dataset_id: scope.datasetId,
      });
    // TODO: cleanup with research_assistant_gen_3 ff
    // Drafts are URL-incapable — ``useCurrentScope`` never produces them.
    // We carry the converters here so the BE→FE focus_scope event handler
    // can round-trip a domain Scope through the wire shape (e.g. when
    // echoing the agent-declared draft back to the slice).
    case 'variable_draft':
      return WireScopeSchema.parse({
        kind: 'variable_draft',
        project_id: scope.projectId,
        client_id: scope.clientId,
        ...(scope.slug !== undefined ? { slug: scope.slug } : {}),
        ...(scope.title !== undefined ? { title: scope.title } : {}),
      });
    case 'cohort_draft':
      return WireScopeSchema.parse({
        kind: 'cohort_draft',
        project_id: scope.projectId,
        client_id: scope.clientId,
        ...(scope.slug !== undefined ? { slug: scope.slug } : {}),
        ...(scope.title !== undefined ? { title: scope.title } : {}),
      });
    case 'concept_group_draft':
      return WireScopeSchema.parse({
        kind: 'concept_group_draft',
        project_id: scope.projectId,
        client_id: scope.clientId,
        ...(scope.slug !== undefined ? { slug: scope.slug } : {}),
        ...(scope.title !== undefined ? { title: scope.title } : {}),
      });
  }
};
